import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row, FormFeedback, Button } from "reactstrap";
import Breadcrumb from 'Components/UI/Breadcrumb';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Select from 'react-select';
import { getBookingQuestion, getQuestionTypes, updateBookingQuestion } from 'helpers/API Calls/System & Settings/Booking Questions';
import Spinner from 'Components/UI/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { STATUS_OPTIONS } from 'pages/Categories/data';

interface BookingQuestionFormValues {
  title: string;
  required: boolean;
  responseType: { label: string; value: string, slug: string } | null;
  showInAdminOnly: boolean;
  hint: string;
  options: Array<{
    id: string;
    answer: string;
    value: string;
    order: string;
  }>;
  status: any;
}

function EditBookingQuestion() {
  const navigate = useNavigate();
  const {id} = useParams();  
  const [options, setOptions] = useState([{ 
    id: crypto.randomUUID(),
    answer: '', 
    value: '', 
    order: ''
  }]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [answerType, setAnswerType] = useState<{ label: string; value: string, slug: string }[]>([]);
  
  const formik = useFormik<BookingQuestionFormValues>({
    initialValues: {
      title: '',
      required: false,
      responseType: null,
      showInAdminOnly: false,
      hint: '',
      options: options,
      status: null, 
    },
    validationSchema: yup.object().shape({
      title: yup.string().required('Question title is required'),
      responseType: yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      }).nullable().required('Please select response type'),
      options: yup.array().when(['responseType'], (
        [responseType], schema) => {
          return responseType?.slug === 'multi_select_input'
            ? schema.min(1, 'At least one option is required').of(
                yup.object().shape({                  answer: yup.string().required('Answer text is required'),
                  value: yup.string(),
                  order: yup.string()
                })
              )
            : schema;
        }
      ),
     status : yup.object().shape({label: yup.string().required(), value: yup.string().required(),}).nullable().required('Please select status'),
      
    }),
    onSubmit: async (values: any, { setSubmitting }) => {
      if (values.responseType?.slug === 'multi_select_input') {
        formik.setFieldTouched('options', true);
      }
      if (Object.keys(formik.errors).length > 0) {
        return;
      }
      setSubmitting(true);
      let payload : any = {
        title: values.title,
        hint: values.hint,
        is_required: values.required,
        is_for_admin: values.showInAdminOnly,
        question_type_id: values.responseType?.value,
        // sequence: 1,
        status: values.status.value,
      }
      if(values.responseType?.slug === 'multi_select_input'){
        payload.booking_question_options = values.options.map((option: any, index: number) => ({
          option: option.answer,
          charges: option.value ? parseFloat(option.value) : null,
          sequence: index,
        }));
      } 
      const response = await updateBookingQuestion(Number(id) ,payload);
      if (response.success) {
        navigate('/booking-questions');
      }
      setSubmitting(false);
    }
  });

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  }, [formik.errors, formik.isSubmitting]);

  const handleOptionChange = (id: string, field: string, value: string) => {
    setOptions(prev => {
      const newOptions = prev.map(opt => 
        opt.id === id ? { ...opt, [field]: value } : opt
      );
      formik.setFieldValue('options', newOptions);
      return newOptions;
    });
  };

  const addOption = () => {    const newOption = { 
      id: crypto.randomUUID(),
      answer: '', 
      value: '', 
      order: ''
    };
    setOptions(prev => [...prev, newOption]);
    formik.setFieldValue('options', [...options, newOption]);
  };

  const removeOption = (id: string) => {
    setOptions(prev => {
      const newOptions = prev.filter(opt => opt.id !== id);
      formik.setFieldValue('options', newOptions);
      return newOptions;
    });
  };

  const moveOption = (id: string, direction: 'up' | 'down') => {
    setOptions(prev => {
      const index = prev.findIndex(opt => opt.id === id);
      if (index === -1) return prev;

      const newOptions = [...prev];
      const [removed] = newOptions.splice(index, 1);
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      newOptions.splice(newIndex, 0, removed);

      formik.setFieldValue('options', newOptions);
      return newOptions;
    });
  };
  
  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);

      const answerTypeOptions = await getQuestionTypes();
      if(answerTypeOptions.success){
        setAnswerType(answerTypeOptions.data?.map((item) => ({ label: item.question_type, value: item.id, slug: item.question_type_slug })));
      }

      const response = await getBookingQuestion(Number(id)); 
      if(response.success){
        const data = response.data;
        const questionType = answerTypeOptions.data.find((item: any) => item.id === data.question_type_id);
        const selectedOptions = data?.options?.map((option: any) => ({
            id: option.id,
            answer: option.option,
            value: option.charges,
            order: option.sequence,
        })) || [];
        selectedOptions.sort((a, b) => a.order - b.order);
        formik.setValues({
          title: data.title, 
          status: {label: data?.status === 'active' ? 'Active' : 'Inactive', value: data?.status}, 
          hint: data.hint,
          required: data.is_required,
          showInAdminOnly: data.is_for_admin,
          responseType: { label: questionType.question_type, value: questionType.id, slug: questionType.question_type_slug },
          options: selectedOptions
        });
        setOptions(selectedOptions);
      }

      setIsLoading(false);
    }
    getData();

  }, [id])

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Booking Questions", link: "/booking-questions" }, { label: "Edit" }]} />
        {isLoading ? <div className="position-relative vh-100"><Spinner/></div>
        :
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              <Row>
                <Col lg="6">
                  {/* Basic Information */}
                  <div className="mb-4">
                    <CardTitle tag="h4" className='mb-4'>Basic Information</CardTitle>
                    
                    <div className="mb-4">
                      <Label htmlFor="title">Question Title <span className="text-danger">*</span></Label>
                      <Input
                        id="title"
                        name="title"
                        type="text"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.title && !!formik.errors.title}
                      />
                      <FormFeedback>{formik.errors.title}</FormFeedback>
                    </div>

                    <div className="mb-4">
                      <Label htmlFor="hint">Question Hint</Label>
                      <Input
                        id="hint"
                        name="hint"
                        type="textarea"
                        rows="3"
                        placeholder="Enter additional information to help users answer this question"
                        value={formik.values.hint}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <CardTitle tag="h4" className='mb-4'>Settings</CardTitle>
                      <div className="mb-4">
                      <Label className="d-block mb-3">Question Behavior</Label>
                      <div className="form-check form-switch mb-3">
                        <Input
                          type="switch"
                          id="required"
                          name="required"
                          checked={formik.values.required}
                          onChange={(e) => {
                            formik.setFieldValue('required', e.target.checked);
                          }}
                        />
                        <Label className="form-check-label" htmlFor="required">
                          Answer Required
                        </Label>
                      </div>

                      <div className="form-check form-switch mb-3">
                        <Input
                          type="switch"
                          id="showInAdminOnly"
                          name="showInAdminOnly"
                          checked={formik.values.showInAdminOnly}
                          onChange={(e) => {
                            formik.setFieldValue('showInAdminOnly', e.target.checked);
                          }}
                        />
                        <Label className="form-check-label" htmlFor="showInAdminOnly">
                          Admin Panel Only
                        </Label>
                      </div>
                    </div>

                    <div className="mb-4">
                      <Label className="form-label">Status <span className="text-danger">*</span></Label>
                      <Select
                        name="status"
                        placeholder="Select Status"
                        options={STATUS_OPTIONS}
                        value={formik.values.status}
                        onChange={(value) => formik.setFieldValue("status", value)}
                        onBlur={formik.handleBlur}
                        className={formik.touched.status && formik.errors.status ? "is-invalid" : ""}
                      />
                      {formik.touched.status && formik.errors.status ? (
                        <FormFeedback type="invalid">{typeof formik.errors.status === 'string' && formik.errors.status}</FormFeedback>
                      ) : null}
                    </div>

                  </div>
                </Col>

                <Col lg="6">
                  
                {/* Response Configuration */}
                <div className="mb-4">
                  <CardTitle tag="h4" className='mb-4'>Response Configuration</CardTitle>
                  
                  <div className="mb-4">
                    <Label>Response Type <span className="text-danger">*</span></Label>
                    <Select
                      id="responseType"
                      name="responseType"
                      options={answerType}
                      value={formik.values.responseType}
                      onChange={(value) => formik.setFieldValue("responseType", value)}
                      onBlur={formik.handleBlur}
                      className={formik.touched.responseType && formik.errors.responseType ? "is-invalid" : ""}
                      placeholder="Select response type"
                    />
                    {formik.touched.responseType && formik.errors.responseType && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.responseType || formik.errors.responseType}
                      </div>
                    )}
                  </div>

                  {formik.values.responseType?.slug === 'multi_select_input' && (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <Label className="mb-0">Response Options <span className="text-danger">*</span></Label>
                        <Button type="button" color="primary" size="sm" onClick={addOption}>
                          <i className="mdi mdi-plus me-1" />
                          Add Option
                        </Button>
                      </div>
                      
                      <div className="table-responsive">
                        <table className="table table-borderless mb-0">
                          <thead>
                            <tr>                              
                              <th>Answer Text</th>
                              <th>Value (£)</th>
                              <th>Display Order</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {options.map((opt) => (
                              <tr key={opt.id}>
                                <td>
                                  <Input
                                    placeholder="Enter answer text"
                                    value={opt.answer}
                                    onChange={(e) => handleOptionChange(opt.id, 'answer', e.target.value)}
                                    onBlur={() => formik.setFieldTouched('options', true)}
                                    invalid={formik.touched.options && (
                                      (typeof formik.errors.options === 'string') ||
                                      (Array.isArray(formik.errors.options) && 
                                        formik.errors.options.some((error: any) => 
                                          error?.answer && opt.id === error.id
                                      ))
                                    )}
                                  />
                                  {formik.touched.options && 
                                    Array.isArray(formik.errors.options) && 
                                    formik.errors.options.some((error: any) => 
                                      error?.answer && opt.id === error.id
                                  ) && (
                                    <FormFeedback>Answer text is required</FormFeedback>
                                  )}
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    placeholder="Value"
                                    value={opt.value}
                                    onChange={(e) => handleOptionChange(opt.id, 'value', e.target.value)}
                                  />
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <Button 
                                      type="button"
                                      color="light"
                                      size="sm"
                                      className="btn-icon"
                                      onClick={() => moveOption(opt.id, 'up')}
                                      disabled={options.indexOf(opt) === 0}
                                    >
                                      <i className="mdi mdi-arrow-up" />
                                    </Button>
                                    <Button 
                                      type="button"
                                      color="light"
                                      size="sm"
                                      className="btn-icon"
                                      onClick={() => moveOption(opt.id, 'down')}
                                      disabled={options.indexOf(opt) === options.length - 1}
                                    >
                                      <i className="mdi mdi-arrow-down" />
                                    </Button>
                                  </div>
                                </td>
                                {/* <td>
                                  <Input
                                    type="number"
                                    placeholder="Bookings"
                                    value={opt.bookings}
                                    onChange={(e) => handleOptionChange(opt.id, 'bookings', e.target.value)}
                                  />
                                </td> */}
                                <td>
                                  <Button 
                                    type="button" 
                                    color="danger" 
                                    size="sm"
                                    className="btn-icon"
                                    onClick={() => removeOption(opt.id)}
                                    disabled={options.length === 1}
                                  >
                                    <i className="mdi mdi-trash-can-outline" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {formik.touched.options && formik.errors.options && (
                        <div className="invalid-feedback d-block">
                          {typeof formik.errors.options === 'string' 
                            ? formik.errors.options 
                            : 'Please fill in all required fields'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </Col>
              
              </Row>
              <div className="d-flex justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Saving...' : 'Save Question'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
    }
      </Container>
    </div>
  );
}

export default EditBookingQuestion;
